import type { TrainingLevel } from '../types/registration';

export const WORKOUT_TRAINING_LEVEL_ORDER: readonly TrainingLevel[] = [
  'beginner',
  'intermediate',
  'advanced',
  'very_advanced',
];

export type WorkoutTrainingLevel = (typeof WORKOUT_TRAINING_LEVEL_ORDER)[number];

export const WORKOUT_TRAINING_LEVEL_RANK: Record<WorkoutTrainingLevel, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
  very_advanced: 3,
};

export const MAX_WORKOUT_LEVELS = 2;

export const WORKOUT_LEVELS_MAX_ERROR = 'Selecione no máximo dois níveis.';

export const WORKOUT_LEVELS_GAP_ERROR =
  'Selecione apenas níveis consecutivos. Não é possível pular um nível (ex.: Iniciante e Avançado).';

const LEGACY_WORKOUT_LEVEL_MAP: Record<string, WorkoutTrainingLevel[]> = {
  beginner: ['beginner'],
  intermediate: ['intermediate'],
  advanced: ['advanced'],
  very_advanced: ['very_advanced'],
  intermediate_advanced: ['intermediate', 'advanced'],
  Iniciante: ['beginner'],
  Intermediario: ['intermediate'],
  Intermediário: ['intermediate'],
  'Intermediario avancado': ['intermediate', 'advanced'],
  'Intermediário avançado': ['intermediate', 'advanced'],
  'Intermediário Avançado': ['intermediate', 'advanced'],
  Avancado: ['advanced'],
  Avançado: ['advanced'],
  'Muito Avancado': ['very_advanced'],
  'Muito Avançado': ['very_advanced'],
};

export const isWorkoutTrainingLevel = (value: string): value is WorkoutTrainingLevel => (
  WORKOUT_TRAINING_LEVEL_ORDER.some((level) => level === value)
);

export const normalizeWorkoutLevels = (values: readonly string[]): WorkoutTrainingLevel[] => {
  const uniqueLevels: WorkoutTrainingLevel[] = [];

  values.forEach((value) => {
    if (!isWorkoutTrainingLevel(value) || uniqueLevels.includes(value)) {
      return;
    }

    uniqueLevels.push(value);
  });

  return uniqueLevels.sort(
    (firstLevel, secondLevel) => WORKOUT_TRAINING_LEVEL_RANK[firstLevel] - WORKOUT_TRAINING_LEVEL_RANK[secondLevel],
  );
};

export const areWorkoutLevelsContiguous = (levels: readonly string[]): boolean => {
  const normalizedLevels = normalizeWorkoutLevels(levels);

  if (normalizedLevels.length === 0 || normalizedLevels.length > MAX_WORKOUT_LEVELS) {
    return false;
  }

  if (normalizedLevels.length === 1) {
    return true;
  }

  return WORKOUT_TRAINING_LEVEL_RANK[normalizedLevels[1]] - WORKOUT_TRAINING_LEVEL_RANK[normalizedLevels[0]] === 1;
};

export const expandLegacyWorkoutLevels = (value?: string | string[] | null): WorkoutTrainingLevel[] => {
  if (Array.isArray(value)) {
    return normalizeWorkoutLevels(value.flatMap((entry) => expandLegacyWorkoutLevels(entry)));
  }

  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return [];
  }

  return normalizeWorkoutLevels(LEGACY_WORKOUT_LEVEL_MAP[trimmedValue] ?? [trimmedValue]);
};

export const resolvePrimaryWorkoutLevel = (levels: readonly string[]): WorkoutTrainingLevel | null => {
  const normalizedLevels = normalizeWorkoutLevels(levels);

  return normalizedLevels[normalizedLevels.length - 1] ?? null;
};
